// Centralized timestamp formatting. All recordings/heartbeats/audit rows store
// UTC ISO-8601; without an explicit timeZone, toLocaleString() renders in the
// VIEWER's browser/OS zone (e.g. UTC on a server console), which looks wrong to
// operators. We pin display to the customer's timezone and show the zone
// abbreviation (CST/CDT) so it's unambiguous. America/Chicago handles the
// daylight-saving switch automatically.
const TZ = "America/Chicago";

type When = string | number | Date | null | undefined;

function toDate(value: When): Date | null {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Date + time, e.g. "5/22/2026, 4:21:07 PM CDT".
export function formatDateTime(value: When): string {
  const d = toDate(value);
  if (!d) return "—";
  return d.toLocaleString("en-US", { timeZone: TZ, timeZoneName: "short" });
}

// Time only, e.g. "4:21:07 PM CDT".
export function formatTime(value: When): string {
  const d = toDate(value);
  if (!d) return "—";
  return d.toLocaleTimeString("en-US", { timeZone: TZ, timeZoneName: "short" });
}

// Today's calendar date in the customer timezone as a YYYY-MM-DD dateBucket.
// Using UTC (new Date().toISOString().slice(0,10)) is wrong: in the Central
// evening the UTC date has already rolled to tomorrow, so "Today" would show
// the next day. en-CA formats as YYYY-MM-DD.
export function centralDateBucket(value: When = new Date()): string {
  const d = toDate(value) ?? new Date();
  return d.toLocaleDateString("en-CA", { timeZone: TZ });
}

// A dateBucket N days before today (Central). Anchors at noon UTC of the
// Central date so plain day subtraction can't trip over DST or midnight.
export function centralBucketDaysAgo(n: number): string {
  const anchor = new Date(`${centralDateBucket()}T12:00:00Z`);
  anchor.setUTCDate(anchor.getUTCDate() - n);
  return anchor.toISOString().slice(0, 10);
}
